/**
 * People of the Dispatch log, a page at a time — ONE paging / live-edge /
 * bounded-continuation implementation for two reads (plan 2026-10-15-004
 * §6.4): one send's people (`GET …/sends/:dispatchId/log`, the send card, the
 * member panel's test result — `useSendLogRows` is that source) and every
 * person's send across the whole play (`GET …/plays/:id/log`, the "All people"
 * table). Originally `useSendLogRows` (plan 2026-10-14-002 U0a):
 *
 * The send card no longer holds every row: the sends poll carries a summary
 * and this loads only the rows a rep asks for — the open card, the chosen
 * filter, "Load more". The filter is applied on the SERVER (the same
 * classifier the chip counts come from), so a chip's number and the rows it
 * opens cannot disagree and a 3,000-person send never has to be in the browser.
 *
 * Server state, so react-query: one infinite query per (send, filter), keyed
 * under `playSendsQueryKey` so Retry / re-check / the post-send invalidation
 * that already refresh the sends refresh an open card's rows with them. While
 * the summary says the send is live the loaded pages refetch every 5 s — the
 * same cadence, and the same stop, as the sends poll itself.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useApi } from '../../../../_shared/hooks';
import { listPlayLogPageRequest, listSendLogPageRequest } from '../../../api-client';
import { playLogQueryKey, sendLogQueryKey } from '../../../dispatch-inspector-logic';
import type { RepDispatchStatus } from '../../../dispatch-rep-status';
import type { PlaybooksDispatchLogRow, PlaybooksPlayLogPageQuery } from '../../../schemas';

/** Rows per page. The server caps a page at 500; a card is read, not exported. */
export const SEND_LOG_PAGE_SIZE = 100;

export interface PlayLogRows {
  rows: readonly PlaybooksDispatchLogRow[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  loadMore: () => void;
  retryLoad: () => void;
}

/** Which rows to read: one send's people, or every send of the play. */
export type PlayLogSource =
  | { kind: 'send'; dispatchId: string | null; status: RepDispatchStatus | 'all' }
  | { kind: 'play'; filters: Pick<PlaybooksPlayLogPageQuery, 'kind' | 'status' | 'memberId'> };

export function usePlayLogRows({
  playId,
  source,
  enabled,
  live,
}: {
  playId: string;
  source: PlayLogSource;
  /** Something is reading the rows (an open card; the table on screen). */
  enabled: boolean;
  /** `summary.live` of the send (or any send of the play) — keep the loaded pages fresh while it is true. */
  live: boolean;
}): PlayLogRows {
  const { apiFetch } = useApi();
  // `null` = nothing to read (a send that predates the dispatch log).
  const sourceKey =
    source.kind === 'send'
      ? source.dispatchId === null
        ? null
        : `send\u0000${source.dispatchId}\u0000${source.status}`
      : `play\u0000${source.filters.kind}\u0000${source.filters.status}\u0000${source.filters.memberId ?? ''}`;
  const active = sourceKey !== null;
  const query = useInfiniteQuery({
    queryKey:
      source.kind === 'send'
        ? sendLogQueryKey(playId, source.dispatchId ?? '', source.status)
        : playLogQueryKey(playId, source.filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const paging = { limit: SEND_LOG_PAGE_SIZE, ...(pageParam === undefined ? {} : { after: pageParam }) };
      return source.kind === 'send'
        ? listSendLogPageRequest(apiFetch, playId, source.dispatchId ?? '', { status: source.status, ...paging })
        : listPlayLogPageRequest(apiFetch, playId, { ...source.filters, ...paging });
    },
    getNextPageParam: (lastPage) => lastPage.nextAfter ?? undefined,
    enabled: enabled && active,
    refetchInterval: live ? 5000 : false,
    // A rep-opened card always shows fresh rows, even if a previous live
    // session left a cached page behind.
    refetchOnMount: 'always',
  });

  // The sends poll and this rows poll are separate queries (review-fix U11,
  // C6-1): when the summary flips live → settled between two row polls, the
  // rows would stay on their last in-flight state. Refetch them ONCE on that
  // edge. The ref holds the previous prop value, read and written in this one
  // effect — no lifetime state, so StrictMode's double setup is a no-op.
  const previousLive = useRef(live);
  const { refetch } = query;
  useEffect(() => {
    const wasLive = previousLive.current;
    previousLive.current = live;
    if (wasLive && !live && enabled && active) void refetch();
  }, [live, enabled, active, refetch]);

  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.rows) ?? [], [query.data]);

  // A FILTERED page can come back short with a cursor (review-fix U9): the
  // server scans at most 5,000 rows per request, so a rare status deep in a
  // huge send is found over several requests. Continue on our own — a rep
  // must never read "No one in this state" while rows remain.
  //
  // BOUNDED (second review-fix pass U10, C6-2): only for an open card with a
  // send, never while a fetch is running or after one failed (react-query
  // leaves `hasNextPage` true after a failed `fetchNextPage`, and an
  // imperative `fetchNextPage` ignores `enabled`), and at most ONCE per
  // cursor. A failed continuation is left to "Try again" / "Load more". The
  // ref is a memo of the last (send, filter, cursor) continued — derived from
  // props and data, no lifetime state, so StrictMode's double setup is a no-op.
  const lastPage = query.data?.pages.at(-1);
  const continueFrom =
    lastPage !== undefined && lastPage.nextAfter !== null && lastPage.rows.length < SEND_LOG_PAGE_SIZE
      ? `${sourceKey ?? ''}\u0000${lastPage.nextAfter}`
      : null;
  const lastContinued = useRef<string | null>(null);
  const { hasNextPage, isFetching, isError, fetchNextPage } = query;
  useEffect(() => {
    if (!enabled || !active || continueFrom === null) return;
    if (!hasNextPage || isFetching || isError || lastContinued.current === continueFrom) return;
    lastContinued.current = continueFrom;
    void fetchNextPage();
  }, [enabled, active, continueFrom, hasNextPage, isFetching, isError, fetchNextPage]);

  return {
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
    // The rep's own "Try again" re-arms the one automatic continuation.
    retryLoad: () => {
      lastContinued.current = null;
      void query.refetch();
    },
  };
}
